import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { RootState, AppDispatch } from '@/store'
import {
  updateBasics,
  updateTargeting,
  updateCreative,
  addSelectedAthlete,
  removeSelectedAthlete,
  updateSelectedAthleteRate,
  addInvitedAthlete,
  removeInvitedAthlete,
  updateInvitedAthleteStatus,
  resetCampaign,
  type CampaignBasics,
  type TargetingOptions,
  type CreativeOptions,
  type SelectedAthlete,
  type InvitedAthlete,
  type AnalyzedBrief,
} from '@/redux/campaignCreationSlice'

/**
 * Custom hook for managing campaign creation state using Redux
 * This replaces the previous CampaignContext functionality
 */
export const useCampaign = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { campaignData } = useSelector((state: RootState) => state.campaignCreation)

  return {
    // State
    campaignData,

    // Actions
    updateBasics: (data: Partial<CampaignBasics>) => dispatch(updateBasics(data)),
    updateTargeting: (data: Partial<TargetingOptions>) => dispatch(updateTargeting(data)),
    updateCreative: (data: Partial<CreativeOptions>) => dispatch(updateCreative(data)),

    // Athlete selection actions
    addSelectedAthlete: (athlete: SelectedAthlete) => dispatch(addSelectedAthlete(athlete)),
    removeSelectedAthlete: (id: string) => dispatch(removeSelectedAthlete(id)),
    updateSelectedAthleteRate: (id: string, rate: number) =>
      dispatch(updateSelectedAthleteRate({ id, rate })),

    // Invited athlete actions
    addInvitedAthlete: (athlete: InvitedAthlete) => dispatch(addInvitedAthlete(athlete)),
    removeInvitedAthlete: (id: string) => dispatch(removeInvitedAthlete(id)),
    updateInvitedAthleteStatus: (id: string, status: 'pending' | 'accepted' | 'declined') =>
      dispatch(updateInvitedAthleteStatus({ id, status })),

    resetCampaign: () => dispatch(resetCampaign()),

    // Navigation helpers
    goToStep: (step: number) => navigate(`/campaign/create/step${step}`),
    goToNextStep: (currentStep: number) => navigate(`/campaign/create/step${currentStep + 1}`),
    goToPreviousStep: (currentStep: number) => navigate(`/campaign/create/step${currentStep - 1}`),
  }
}

// Re-export types for convenience
export type {
  CampaignBasics,
  TargetingOptions,
  CreativeOptions,
  CampaignBriefData,
  SelectedAthlete,
  InvitedAthlete,
  AnalyzedBrief,
} from '@/redux/campaignCreationSlice'
